import { Suspense, lazy, useEffect } from 'react'
import { Link, Navigate, Route, Routes } from 'react-router-dom'
import Layout from '@/components/layout/Layout'
import Login from '@/pages/Login'
import Dashboard from '@/pages/Dashboard'
import Orders from '@/pages/Orders'
import OrderDetail from '@/pages/OrderDetail'
import Products from '@/pages/Products'
import Conversations from '@/pages/Conversations'
import Customers from '@/pages/Customers'
import CustomerForm from '@/pages/CustomerForm'
import TransportServices from '@/pages/TransportServices'
import TransportServiceForm from '@/pages/TransportServiceForm'
import TransportLoads from '@/pages/TransportLoads'
import SalesReports from '@/pages/SalesReports'
import Messages from '@/pages/Messages'
import MatchTester from '@/pages/MatchTester'
import Categories from '@/pages/Categories'
import Units from '@/pages/Units'
import UnitWeights from '@/pages/UnitWeights'
import CustomerOrder from '@/pages/public/CustomerOrder'
import PrivacyPolicy from '@/pages/public/PrivacyPolicy'
import Playground from '@/pages/Playground'
import AiCalls from '@/pages/AiCalls'
import Users from '@/pages/Users'
import Roles from '@/pages/Roles'
import Account from '@/pages/Account'
import Settings from '@/pages/Settings'
import { PERMS, can, isSuperAdmin, useAuth } from '@/store/auth'


function RequireAuth({ children }) {
  const { user, loading } = useAuth()
  if (loading) return <div className="page-loading">Loading…</div>
  if (!user) return <Navigate to="/login" replace />
  return children
}

/**
 * Route-level gate. The API enforces this too — this is so a viewer who
 * types /users, or follows an old bookmark after a role change, gets an
 * explanation instead of a screen of 403s.
 */
function RequirePermission({ permission, children }) {
  const { user } = useAuth()
  // A super admin holds every permission, including ones added after this
  // build shipped, so it never needs to appear in the list explicitly.
  if (!can(user, permission) && !isSuperAdmin(user)) return <Forbidden />
  return children
}

/**
 * Home. The dashboard for a role that may see it; otherwise the first menu
 * the role can open, in sidebar order — a packer lands on Orders rather than
 * on a 'not available' page. My account is open to everyone, so it is last.
 */
const HOME_FALLBACKS = [
  [PERMS.ordersRead, '/orders'],
  [PERMS.messagesRead, '/messages'],
  [PERMS.customersRead, '/customers'],
  [PERMS.productsRead, '/products'],
  [PERMS.aiRead, '/ai-activity'],
  [PERMS.usersRead, '/users'],
  [PERMS.rolesRead, '/roles'],
  [PERMS.settingsWrite, '/settings'],
]

function Home() {
  const { user } = useAuth()
  if (isSuperAdmin(user) || can(user, PERMS.dashboardRead)) return <Dashboard />
  const first = HOME_FALLBACKS.find(([permission]) => can(user, permission))
  return <Navigate to={first ? first[1] : '/account'} replace />
}

function Forbidden() {
  const { user } = useAuth()
  return (
    <div className="forbidden">
      <div className="empty-icon">🔒</div>
      <h2>Not available to your role</h2>
      <p className="muted">
        You are signed in as {user?.role}. Ask an admin if you need access to this screen.
      </p>
      <Link to="/" className="btn">
        Back to the start
      </Link>
    </div>
  )
}

export default function App() {
  const init = useAuth((s) => s.init)

  useEffect(() => {
    init()
  }, [init])

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* Public on purpose: the customer who opens this has no account, and
          the link token is what identifies the order. Kept outside <Layout>
          so no sidebar, no auth store and no staff screens come with it. */}
      <Route path="/o/:token" element={<CustomerOrder />} />
      {/* Public, as Meta requires for a WhatsApp Business app. */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route index element={<Home />} />
        <Route
          path="orders"
          element={
            <RequirePermission permission={PERMS.ordersRead}>
              <Orders />
            </RequirePermission>
          }
        />
        <Route
          path="orders/:id"
          element={
            <RequirePermission permission={PERMS.ordersRead}>
              <OrderDetail />
            </RequirePermission>
          }
        />
        <Route
          path="products"
          element={
            <RequirePermission permission={PERMS.productsRead}>
              <Products />
            </RequirePermission>
          }
        />
        <Route path="products/match-tester" element={<MatchTester />} />
        <Route
          path="products/categories"
          element={
            <RequirePermission permission={PERMS.productsWrite}>
              <Categories />
            </RequirePermission>
          }
        />
        <Route
          path="products/units"
          element={
            <RequirePermission permission={PERMS.productsWrite}>
              <Units />
            </RequirePermission>
          }
        />
        <Route
          path="products/unit-weights"
          element={
            <RequirePermission permission={PERMS.productsWrite}>
              <UnitWeights />
            </RequirePermission>
          }
        />
        <Route
          path="test-bench"
          element={
            <RequirePermission permission={PERMS.playgroundRun}>
              <Playground />
            </RequirePermission>
          }
        />
        <Route
          path="ai-activity"
          element={
            <RequirePermission permission={PERMS.aiRead}>
              <AiCalls />
            </RequirePermission>
          }
        />
        <Route
          path="customers"
          element={
            <RequirePermission permission={PERMS.customersRead}>
              <Customers />
            </RequirePermission>
          }
        />
        <Route
          path="customers/new"
          element={
            <RequirePermission permission={PERMS.customersWrite}>
              <CustomerForm />
            </RequirePermission>
          }
        />
        <Route
          path="customers/:id"
          element={
            <RequirePermission permission={PERMS.customersRead}>
              <CustomerForm />
            </RequirePermission>
          }
        />
        <Route
          path="transport-services"
          element={
            <RequirePermission permission={PERMS.deliveriesRead}>
              <TransportServices />
            </RequirePermission>
          }
        />
        <Route
          path="sales-reports"
          element={
            <RequirePermission permission={PERMS.reportsRead}>
              <SalesReports />
            </RequirePermission>
          }
        />
        <Route
          path="transport-loads"
          element={
            <RequirePermission permission={PERMS.transportLoadsRead}>
              <TransportLoads />
            </RequirePermission>
          }
        />
        <Route
          path="transport-services/new"
          element={
            <RequirePermission permission={PERMS.deliveriesWrite}>
              <TransportServiceForm />
            </RequirePermission>
          }
        />
        <Route
          path="transport-services/:id"
          element={
            <RequirePermission permission={PERMS.deliveriesRead}>
              <TransportServiceForm />
            </RequirePermission>
          }
        />
        <Route
          path="messages"
          element={
            <RequirePermission permission={PERMS.messagesRead}>
              <Conversations />
            </RequirePermission>
          }
        />
        {/* The raw message log, still reachable for the case it was
            built for: a number nobody has registered yet. */}
        <Route
          path="messages/raw"
          element={
            <RequirePermission permission={PERMS.messagesRead}>
              <Messages />
            </RequirePermission>
          }
        />
        <Route path="account" element={<Account />} />
        <Route
          path="users"
          element={
            <RequirePermission permission={PERMS.usersRead}>
              <Users />
            </RequirePermission>
          }
        />
        <Route
          path="roles"
          element={
            <RequirePermission permission={PERMS.rolesRead}>
              <Roles />
            </RequirePermission>
          }
        />
        <Route
          path="settings"
          element={
            <RequirePermission permission={PERMS.settingsWrite}>
              <Settings />
            </RequirePermission>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
